import type {
  AnnotationUser,
  CommentSummary,
  CreateCommentRequest,
  CreateTaskRequest,
  MyTasksPage,
  NoteSummary,
  TaskSummary,
  UpdateTaskRequest,
} from '@ofmos/shared/annotations';
import type { AnnotationClient, MyTaskCountFilter, MyTasksFilter } from '../client';
import type { Transport } from './transport';

/**
 * `AnnotationClient` over the notes/tasks/comments surface (notes-tasks-comments.md
 * section 10).
 *
 * There is no `mapping.ts` step here and that is the design, not an omission: an
 * annotation is CONTENT, not a record. It carries no field metadata ids, no provenance map
 * and no `values` keyed by uuid, so the summary the server sends IS the summary the screens
 * render - the shared type is the wire type. Every mapping function in this directory
 * exists to bridge a difference between the two models, and here there is none to bridge.
 *
 * Thin like the webhook and pending clients: the authorship rule (2.5), the optimistic
 * lock (2.6) and D9's my-tasks visibility are all server-side, and a second opinion in the
 * browser could only ever be a stale one. The screens surface the refusal.
 */
export class HttpAnnotationClient implements AnnotationClient {
  constructor(private readonly transport: Transport) {}

  // --- notes -----------------------------------------------------------------

  async listNotes(objectId: string, recordId: string): Promise<readonly NoteSummary[]> {
    const response = await this.transport.request<{ notes: readonly NoteSummary[] }>({
      method: 'GET',
      path: `/api/records/${objectId}/${recordId}/notes`,
    });
    return response.notes;
  }

  async createNote(objectId: string, recordId: string, body: string): Promise<NoteSummary> {
    const response = await this.transport.request<{ note: NoteSummary }>({
      method: 'POST',
      path: `/api/records/${objectId}/${recordId}/notes`,
      body: { body },
    });
    return response.note;
  }

  /**
   * `ifUnmodifiedSince` is ABSENT rather than null when the caller has nothing to claim:
   * the body schema is a `strictObject`, and sending the key with an empty value would
   * turn "I did not read a version" into a version claim the server has to reject.
   */
  async updateNote(noteId: string, body: string, ifUnmodifiedSince?: string): Promise<NoteSummary> {
    const response = await this.transport.request<{ note: NoteSummary }>({
      method: 'PATCH',
      path: `/api/notes/${noteId}`,
      body: { body, ...(ifUnmodifiedSince === undefined ? {} : { ifUnmodifiedSince }) },
    });
    return response.note;
  }

  async deleteNote(noteId: string): Promise<void> {
    await this.transport.requestMaybe({ method: 'DELETE', path: `/api/notes/${noteId}` });
  }

  // --- tasks -----------------------------------------------------------------

  async listTasks(objectId: string, recordId: string): Promise<readonly TaskSummary[]> {
    const response = await this.transport.request<{ tasks: readonly TaskSummary[] }>({
      method: 'GET',
      path: `/api/records/${objectId}/${recordId}/tasks`,
    });
    return response.tasks;
  }

  async createTask(
    objectId: string,
    recordId: string,
    input: CreateTaskRequest,
  ): Promise<TaskSummary> {
    const response = await this.transport.request<{ task: TaskSummary }>({
      method: 'POST',
      path: `/api/records/${objectId}/${recordId}/tasks`,
      body: input,
    });
    return response.task;
  }

  /**
   * The patch is sent VERBATIM. `null` means clear and an absent key means leave alone, so
   * normalising the object here - filling in defaults, dropping nulls - would silently
   * turn "unassign this task" into "do not touch the assignee".
   */
  async updateTask(taskId: string, patch: UpdateTaskRequest): Promise<TaskSummary> {
    const response = await this.transport.request<{ task: TaskSummary }>({
      method: 'PATCH',
      path: `/api/tasks/${taskId}`,
      body: patch,
    });
    return response.task;
  }

  async deleteTask(taskId: string): Promise<void> {
    await this.transport.requestMaybe({ method: 'DELETE', path: `/api/tasks/${taskId}` });
  }

  /** The one route that answers with its page envelope directly rather than wrapped. */
  async listMyTasks(query: MyTasksFilter): Promise<MyTasksPage> {
    return this.transport.request<MyTasksPage>({
      method: 'GET',
      path: '/api/tasks',
      query: {
        assignee: query.assignee,
        // The route's schema takes the STRINGS `true` / `false`, because a query string
        // has no booleans and a coercion that treats `false` as truthy is the classic way
        // to ship a filter that never filters.
        open: query.open ? 'true' : 'false',
        ...(query.limit === undefined ? {} : { limit: query.limit }),
        ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
        ...(query.dueTo === undefined ? {} : { dueTo: query.dueTo }),
        ...(query.kind === undefined ? {} : { kind: query.kind }),
      },
    });
  }

  /**
   * NO FILTER IS THE BADGE, and the absence has to reach the wire as an absence: the
   * server's defaults are `assignee=me` with no window, so an empty query string is the
   * number `TaskBadge` has always shown.
   */
  async countMyOpenTasks(filter?: MyTaskCountFilter): Promise<number> {
    const response = await this.transport.request<{ count: number }>({
      method: 'GET',
      path: '/api/tasks/count',
      ...(filter === undefined
        ? {}
        : {
            query: {
              assignee: filter.assignee,
              ...(filter.dueTo === undefined ? {} : { dueTo: filter.dueTo }),
              ...(filter.kind === undefined ? {} : { kind: filter.kind }),
            },
          }),
    });
    return response.count;
  }

  // --- comments --------------------------------------------------------------

  async listComments(
    objectId: string,
    recordId: string,
    noteId?: string,
  ): Promise<readonly CommentSummary[]> {
    const response = await this.transport.request<{ comments: readonly CommentSummary[] }>({
      method: 'GET',
      path: `/api/records/${objectId}/${recordId}/comments`,
      query: noteId === undefined ? {} : { noteId },
    });
    return response.comments;
  }

  async createComment(
    objectId: string,
    recordId: string,
    input: CreateCommentRequest,
  ): Promise<CommentSummary> {
    const response = await this.transport.request<{ comment: CommentSummary }>({
      method: 'POST',
      path: `/api/records/${objectId}/${recordId}/comments`,
      body: input,
    });
    return response.comment;
  }

  async updateComment(
    commentId: string,
    body: string,
    ifUnmodifiedSince?: string,
  ): Promise<CommentSummary> {
    const response = await this.transport.request<{ comment: CommentSummary }>({
      method: 'PATCH',
      path: `/api/comments/${commentId}`,
      body: { body, ...(ifUnmodifiedSince === undefined ? {} : { ifUnmodifiedSince }) },
    });
    return response.comment;
  }

  async deleteComment(commentId: string): Promise<void> {
    await this.transport.requestMaybe({ method: 'DELETE', path: `/api/comments/${commentId}` });
  }

  // --- users -----------------------------------------------------------------

  /** No emails on this route by design: the product names people here, it does not mail them. */
  async listUsers(): Promise<readonly AnnotationUser[]> {
    const response = await this.transport.request<{ users: readonly AnnotationUser[] }>({
      method: 'GET',
      path: '/api/annotations/users',
    });
    return response.users;
  }
}
